import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, List, ListItem, ListItemButton, ListItemIcon, ListItemText, CircularProgress, ClickAwayListener } from '@mui/material';
import './InputAutoCompleteCard.scss';

export default function InputAutoCompleteCard({ 
    open, 
    options = [], 
    onClose, 
    onOptionClick,
    highlightedIndex = -1,
    containerRef
}) {
    const { t } = useTranslation('fragments');
    const [loadingItemId, setLoadingItemId] = useState(null);
    const listRef = useRef(null);
    const highlightedItemRef = useRef(null);

    // Scroll para o item destacado quando o índice mudar
    useEffect(() => {
        if (highlightedIndex >= 0 && highlightedItemRef.current && listRef.current) {
            const listElement = listRef.current;
            const itemElement = highlightedItemRef.current;
            
            const listRect = listElement.getBoundingClientRect();
            const itemRect = itemElement.getBoundingClientRect();
            
            // Verificar se o item está visível
            if (itemRect.top < listRect.top) {
                // Item está acima da área visível, scroll para cima
                listElement.scrollTop = itemElement.offsetTop - listElement.offsetTop;
            } else if (itemRect.bottom > listRect.bottom) {
                // Item está abaixo da área visível, scroll para baixo
                listElement.scrollTop = itemElement.offsetTop - listElement.offsetTop - (listRect.height - itemRect.height);
            }
        }
    }, [highlightedIndex]);

    // Função para lidar com o clique em uma opção
    const handleOptionClick = async (option) => {
        if (option.disabled || loadingItemId !== null || !option.onClick) return;

        try {
            setLoadingItemId(option.id);
            if (onOptionClick) {
                await onOptionClick(option);
            } else if (option.onClick) {
                await option.onClick();
            }
        } catch (error) {
            console.error('Erro ao executar onClick:', error);
        } finally {
            setLoadingItemId(null);
        }
    };

    // Função para renderizar o ícone da opção
    const renderIcon = (option) => {
        if (option.icon) {
            return (
                <ListItemIcon
                    sx={{
                        minWidth: 36,
                        color: 'inherit'
                    }}
                >
                    {option.icon}
                </ListItemIcon>
            );
        }
        return null;
    };

    // Função para renderizar o loader
    const renderLoader = (option) => {
        if (loadingItemId === option.id) {
            return (
                <CircularProgress
                    size={16}
                    sx={{
                        ml: 1,
                        color: 'rgba(255, 255, 255, 0.7)'
                    }}
                />
            );
        }
        return null;
    };

    // Sempre renderizar o componente, mas controlar a visibilidade via CSS
    // Isso garante que o componente esteja no DOM e possa ser posicionado corretamente

    // Função para verificar se o clique foi no autocomplete
    const handleClickAway = (event) => {
        // Verificar se o clique foi no próprio autocomplete
        const autocompleteCard = event.target.closest('.input-autocomplete-card');
        if (autocompleteCard) {
            return; // Não fechar se o clique foi no autocomplete
        }
        
        // Verificar se o clique foi no container do input ou em algum elemento dentro dele
        if (containerRef?.current && (containerRef.current.contains(event.target) || containerRef.current === event.target)) {
            return; // Não fechar se o clique foi no input ou no container
        }
        
        // Fechar apenas se o clique foi fora de ambos
        onClose();
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div className={`input-autocomplete-card ${open ? 'open' : 'closed'}`} style={{ display: open ? 'block' : 'none' }}>
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        border: 'none',
                        overflow: 'hidden',
                        width: '100%',
                        maxHeight: '320px',
                        boxShadow: 'none',
                    }}
                >
                    <List 
                        ref={listRef}
                        sx={{
                            p: 0,
                            maxHeight: '320px',
                            overflow: 'auto',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                        }}
                    >
                        {options.length === 0 ? (
                            <ListItem disablePadding>
                                <ListItemButton disabled sx={{ py: 1.1, px: 2 }}>
                                    <ListItemText
                                        primary={t('input.autocomplete.empty')}
                                        sx={{
                                            '& .MuiListItemText-primary': {
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: 'rgba(255, 255, 255, 0.4)',
                                            }
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ) : (
                            options.map((option, index) => {
                                const isHighlighted = index === highlightedIndex;
                                return (
                                    <ListItem
                                        key={option.id || index}
                                        disablePadding
                                        ref={isHighlighted ? highlightedItemRef : null}
                                        sx={{
                                            borderBottom: index < options.length - 1
                                                ? '1px solid rgba(255, 255, 255, 0.1)'
                                                : 'none'
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => handleOptionClick(option)}
                                            disabled={option.disabled || loadingItemId !== null}
                                            className={`autocomplete-option-${option.variant || 'default'} ${isHighlighted ? 'highlighted' : ''}`}
                                            sx={{
                                                py: 1.1,
                                                px: 2,
                                                color: option.disabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                                                backgroundColor: isHighlighted ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: option.disabled ? 'transparent' : (isHighlighted ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.08)'),
                                                },
                                                '&.Mui-disabled': {
                                                    opacity: 0.5,
                                                }
                                            }}
                                        >
                                            {renderIcon(option)}
                                            <ListItemText
                                                primary={option.label}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                    }
                                                }}
                                            />
                                            {renderLoader(option)}
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })
                        )}
                    </List>
                </Paper>
            </div>
        </ClickAwayListener>
    );
}

